import { useEffect, useRef, useState } from 'react';

const NAV_ITEMS = [
  { label: 'Home', href: '#home' },
  { label: 'Services', href: '#services' },
  { label: 'Why Us', href: '#why-vegaralabs' },
  { label: 'About', href: '#about-us' },
  { label: 'FAQ', href: '#support' },
];

const aboutStats = [
  { value: 100, suffix: '+', label: 'Projects Completed', icon: 'doc', tone: 'orange' },
  { value: 90, suffix: '+', label: 'Trusted Partners', icon: 'team' },
  { value: 350, suffix: '+', label: 'Guidelines Created', icon: 'screen' },
  { value: 8, suffix: '+', label: 'Years of Experience', icon: 'award', pad: 2 },
];

const WEB3FORMS_ACCESS_KEY = '227eb3c1-76e5-4718-9ce4-294bc2501758';

const whyStats = [
  { value: 5, suffix: '+', label: 'years of industry experience', wide: true },
  { value: 10, suffix: '+', label: 'brand partners' },
  { value: 10, suffix: '+', label: 'team members' },
];

const faqs = [
  {
    question: 'How do your web solutions help my business grow?',
    answer: 'We design systems around real business workflows, then connect booking, content, customer journeys, and admin tools so your team can move faster.',
  },
  {
    question: 'Can you build a custom website for any business type?',
    answer: 'Yes. We shape the structure, features, and visual direction around your brand, audience, and daily operations instead of forcing a generic template.',
  },
  {
    question: 'What services can I combine in one project?',
    answer: 'You can combine design, development, marketing, video, dashboards, automation, and support into one clear launch plan.',
  },
  {
    question: 'Do you help after the website is launched?',
    answer: 'Yes. We can support updates, performance improvements, new pages, content changes, analytics, and campaign landing pages after launch.',
  },
  {
    question: 'How long does a typical project take?',
    answer: 'Timelines depend on scope, but smaller landing pages can move quickly, while custom dashboards or full platforms are planned in focused milestones.',
  },
];

const services = [
  {
    id: 1,
    title: 'Design That Gets Attention',
    description: 'Logo design, brand identity, UI/UX design.',
    meta: 'Logo Design • Brand Identity • UI/UX Design',
    image: '/images/service-design.png',
    statOne: '100%',
    statOneLabel: 'Bespoke',
    statTwo: '48h',
    statTwoLabel: 'First draft',
    statThree: '24/7',
    statThreeLabel: 'Support',
    included: ['Logo & Brand Identity', 'UI/UX Design', 'Print & Packaging', 'Social Media Creatives'],
  },
  {
    id: 2,
    title: 'Development That Solves Problems',
    description: 'Web apps, mobile apps, custom software.',
    meta: 'Web Dev • Mobile Apps • Custom Software',
    image: '/images/service-development.png',
    statOne: '99%',
    statOneLabel: 'Reliable',
    statTwo: 'Agile',
    statTwoLabel: 'Delivery',
    statThree: 'API',
    statThreeLabel: 'Ready',
    included: ['Web Applications', 'Mobile App Interfaces', 'Admin Dashboards', 'Custom Software'],
  },
  {
    id: 3,
    title: 'Marketing That Drives Growth',
    description: 'SEO, Google Ads, social media campaigns.',
    meta: 'SEO • Google Ads • Social Media',
    image: '/images/service-marketing.png',
    statOne: 'ROI',
    statOneLabel: 'Focused',
    statTwo: 'Live',
    statTwoLabel: 'Tracking',
    statThree: '360',
    statThreeLabel: 'Campaigns',
    included: ['Search Engine Optimisation', 'Paid Ad Campaigns', 'Social Media Strategy', 'Analytics & Reporting'],
  },
  {
    id: 4,
    title: 'Videos That Stop The Scroll',
    description: 'Motion graphics, promo videos, reels.',
    meta: 'Motion Graphics • Promo Videos • Reels',
    image: '/images/service-video.png',
    statOne: '4K',
    statOneLabel: 'Ready',
    statTwo: 'Fast',
    statTwoLabel: 'Edits',
    statThree: 'Reels',
    statThreeLabel: 'Optimised',
    included: ['Motion Graphics', 'Promotional Videos', 'Short-Form Reels', 'Launch Visuals'],
  },
];

function ServicesOrbitSection() {
  const [activeService, setActiveService] = useState(null);

  useEffect(() => {
    if (!activeService) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setActiveService(null);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [activeService]);

  return (
    <section className="services-orbit" id="services" aria-labelledby="services-title">
      <div className="services-orbit-head">
        <div className="services-kicker">Our Services</div>
        <h2 id="services-title">
          <span>Your brand deserves <em>more</em></span>
          <span>than</span>
          <span>generic <em>visuals</em></span>
        </h2>
      </div>

      <div className="services-grid" aria-label="Service cards">
        {services.map((service) => (
          <article className="service-grid-card" key={service.id}>
            <div className="service-card-inner">
              <div className="service-card-art" aria-hidden="true">
                <img src={service.image} alt="" />
              </div>
              <div className="service-card-meta">{service.meta}</div>
              <span className="service-card-number">{String(service.id).padStart(2, '0')}</span>
              <span className="service-card-icon" aria-hidden="true" />
              <h3>{service.title}</h3>
              <p>{service.description}</p>
              <button
                type="button"
                className="service-detail-btn"
                onClick={() => setActiveService(service)}
              >
                See details
              </button>
            </div>
          </article>
        ))}
      </div>

      {activeService && (
        <div
          className="service-modal-backdrop"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setActiveService(null);
          }}
        >
          <article className="service-modal" role="dialog" aria-modal="true" aria-labelledby="service-modal-title">
            <button
              type="button"
              className="service-modal-close"
              aria-label="Close service details"
              onClick={() => setActiveService(null)}
            >
              ×
            </button>

            <div className="service-modal-kicker">Our Services</div>
            <h3 id="service-modal-title">{activeService.title.replace(' That ', ' ')}</h3>
            <div className="service-modal-meta">{activeService.meta}</div>
            <p className="service-modal-intro">
              We shape this service around your business goals, customer journey, and brand
              direction so every deliverable feels practical, polished, and ready to launch.
            </p>

            <div className="service-modal-stats">
              <div>
                <strong>{activeService.statOne}</strong>
                <span>{activeService.statOneLabel}</span>
              </div>
              <div>
                <strong>{activeService.statTwo}</strong>
                <span>{activeService.statTwoLabel}</span>
              </div>
              <div>
                <strong>{activeService.statThree}</strong>
                <span>{activeService.statThreeLabel}</span>
              </div>
            </div>

            <div className="service-modal-grid">
              <div>
                <h4>What's included</h4>
                <ul>
                  {activeService.included.map((item) => (
                    <li key={item}>
                      <span />
                      <div>
                        <strong>{item}</strong>
                        <p>Built with a clean process and a premium finish.</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h4>How we work</h4>
                <ol>
                  {['Discovery', 'Concept', 'Build', 'Deliver'].map((step, index) => (
                    <li key={step}>
                      <span>{String(index + 1).padStart(2, '0')}</span>
                      <div>
                        <strong>{step}</strong>
                        <p>Clear direction, fast feedback loops, and launch-ready execution.</p>
                      </div>
                    </li>
                  ))}
                </ol>
              </div>
            </div>

            <footer className="service-modal-footer">
              <span>Let's build something great together.</span>
              <div>
                <button type="button" className="service-modal-ghost" onClick={() => setActiveService(null)}>
                  Close
                </button>
                <button type="button" className="service-modal-contact">
                  Contact Us
                </button>
              </div>
            </footer>
          </article>
        </div>
      )}
    </section>
  );
}

function CountUpNumber({ value, suffix = '', active, pad = 0 }) {
  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    if (!active) {
      setDisplayValue(0);
      return undefined;
    }

    const duration = 1100;
    const start = performance.now();
    let frameId;

    const tick = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplayValue(Math.round(value * eased));

      if (progress < 1) {
        frameId = requestAnimationFrame(tick);
      }
    };

    frameId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frameId);
  }, [active, value]);

  return <>{String(displayValue).padStart(pad, '0')}{suffix}</>;
}

function WhyChooseSection() {
  const sectionRef = useRef(null);
  const [statsActive, setStatsActive] = useState(false);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setStatsActive(true);
      },
      { threshold: 0.35 }
    );

    observer.observe(section);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="why-choose" id="why-vegaralabs" aria-labelledby="why-title" ref={sectionRef}>
      <div className="why-grid-bg" aria-hidden="true" />
      <div className="why-inner">
        <div className="why-copy">
          <h2 id="why-title">
            <span>Why</span>
            <span>Choose</span>
            <span><b>→</b><em>Us</em></span>
          </h2>

          <div className="why-stats">
            {whyStats.map((stat) => (
              <div className={`why-stat ${stat.wide ? 'wide' : ''} ${statsActive ? 'is-visible' : ''}`} key={stat.label}>
                <strong>
                  <CountUpNumber value={stat.value} suffix={stat.suffix} active={statsActive} />
                </strong>
                <span>{stat.label}</span>
              </div>
            ))}
          </div>

          <a className="why-visit" href="#services">
            <span>↗</span>
            Visit Site
          </a>
        </div>

        <div className="why-astronauts" aria-hidden="true">
          <img className="why-dotted-globe" src="/images/why-dotted-globe.png" alt="" />
          <div className="astro-orbit orbit-one">
            <img className="why-astronaut astro-one" src="/images/why-astronaut-1.png" alt="" />
          </div>
          <div className="astro-orbit orbit-two">
            <img className="why-astronaut astro-two" src="/images/why-astronaut-2.png" alt="" />
          </div>
          <div className="astro-orbit orbit-three">
            <img className="why-astronaut astro-three" src="/images/why-astronaut-3.png" alt="" />
          </div>
        </div>

        <div className="why-panel">
          <p>
            We design and build web solutions that help businesses feel organised,
            trusted, and ready to grow. From strategy to launch, every detail is shaped
            around clarity, speed, and measurable impact.
          </p>
        </div>
      </div>
    </section>
  );
}

function AboutSection() {
  const sectionRef = useRef(null);
  const [statsActive, setStatsActive] = useState(false);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setStatsActive(true);
      },
      { threshold: 0.35 }
    );

    observer.observe(section);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="about-agency" id="about-us" aria-labelledby="about-title" ref={sectionRef}>
      <div className="about-shell">
        <div className="about-head">
          <div>
            <div className="about-kicker">— About Us —</div>
            <h2 id="about-title">
              Creative Strategy For <span>Unique Brands</span>
            </h2>
          </div>
          <p>
            We combine design, strategy, and smart technology to help businesses stand
            out. Our solutions turn ideas into digital experiences that connect, inspire,
            and leave a lasting impact.
          </p>
        </div>

        <div className="about-card-row" aria-label="Company highlights">
          {aboutStats.map((stat, index) => (
            <article
              className={`about-stat-card about-card-${index + 1} ${stat.tone || ''} ${statsActive ? 'is-visible' : ''}`}
              key={stat.label}
            >
              <span className={`about-stat-icon icon-${stat.icon}`} aria-hidden="true" />
              <strong>
                <CountUpNumber value={stat.value} suffix={stat.suffix} active={statsActive} pad={stat.pad} />
              </strong>
              <p>{stat.label}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function FaqSection() {
  const [openFaq, setOpenFaq] = useState(2);

  return (
    <section className="faq-section" id="support" aria-labelledby="faq-title">
      <div className="faq-orbital-bg" aria-hidden="true">
        <img src="/images/faq-3d-ring.png" alt="" />
        <span className="faq-mini-orb orb-one" />
        <span className="faq-mini-orb orb-two" />
      </div>

      <div className="faq-inner">
        <div className="faq-copy">
          <div className="faq-kicker">FAQs</div>
          <h2 id="faq-title">
            Frequently asked <span>questions</span>
          </h2>
          <p>
            Clear answers about strategy, timelines, support, and how we build web
            solutions around the way your business actually works.
          </p>
        </div>

        <div className="faq-list" aria-label="Frequently asked questions">
          {faqs.map((faq, index) => {
            const isOpen = openFaq === index;
            return (
              <article className={`faq-item ${isOpen ? 'open' : ''}`} key={faq.question}>
                <button
                  type="button"
                  className="faq-question"
                  aria-expanded={isOpen}
                  onClick={() => setOpenFaq(isOpen ? null : index)}
                >
                  <span>{faq.question}</span>
                  <b aria-hidden="true">{isOpen ? '×' : '+'}</b>
                </button>
                <div className="faq-answer">
                  <p>{faq.answer}</p>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function ContactSection() {
  const [submitState, setSubmitState] = useState({ status: 'idle', message: '' });

  const handleContactSubmit = async (event) => {
    event.preventDefault();
    setSubmitState({ status: 'sending', message: 'Sending your message...' });

    const form = event.currentTarget;
    const formData = new FormData(form);
    formData.append('access_key', WEB3FORMS_ACCESS_KEY);
    formData.append('subject', 'New project brief from VegaraLabs website');
    formData.append('from_name', 'VegaraLabs Contact Form');

    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        body: formData,
      });
      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.message || 'Unable to send message.');
      }

      form.reset();
      setSubmitState({
        status: 'success',
        message: 'Thank you. Your message has been sent.',
      });
    } catch (error) {
      setSubmitState({
        status: 'error',
        message: 'Sorry, your message could not be sent. Please email us directly.',
      });
    }
  };

  return (
    <section className="contact-section" id="contact" aria-labelledby="contact-title">
      <div className="contact-hero">
        <span aria-hidden="true" />
        <h2 id="contact-title">Start Your Project</h2>
      </div>

      <div className="contact-inner">
        <div className="contact-copy">
          <div className="contact-kicker">Build With VegaraLabs</div>
          <h3>Tell Us What Your Business Needs Next</h3>
          <p>
            Share the idea, challenge, or workflow you want to improve. We can shape it into
            a sharp brand experience, booking system, dashboard, campaign, or launch-ready
            digital product.
          </p>

          <div className="contact-methods">
            <a className="contact-method dark" href="mailto:vegaralabs@gmail.com">
              <span aria-hidden="true">✉</span>
              <div>
                <strong>Email</strong>
                <small>vegaralabs@gmail.com</small>
              </div>
            </a>
            <a className="contact-method accent" href="tel:0742705021">
              <span aria-hidden="true">●</span>
              <div>
                <strong>Phone</strong>
                <small>0742705021</small>
              </div>
            </a>
          </div>

          <div className="contact-project-card" aria-label="Project planning preview">
            <div>
              <span>01</span>
              <strong>Discovery</strong>
              <small>Goals, audience, scope</small>
            </div>
            <div>
              <span>02</span>
              <strong>Design & Build</strong>
              <small>Brand, website, system</small>
            </div>
            <div>
              <span>03</span>
              <strong>Launch Support</strong>
              <small>Testing, updates, growth</small>
            </div>
          </div>
        </div>

        <form className="contact-form" onSubmit={handleContactSubmit}>
          <input type="hidden" name="botcheck" tabIndex="-1" autoComplete="off" />
          <label>
            <span>Name</span>
            <input type="text" name="name" placeholder="Your Name" required />
          </label>
          <label>
            <span>Email</span>
            <input type="email" name="email" placeholder="Your Email" required />
          </label>
          <label>
            <span>Message</span>
            <textarea name="message" placeholder="Tell us about your website, app, dashboard, campaign, or video idea" rows="6" required />
          </label>

          <fieldset>
            <legend>What do you need?</legend>
            <label>
              <input type="checkbox" name="topic" value="project" />
              <span>Website or app</span>
            </label>
            <label>
              <input type="checkbox" name="topic" value="consultation" />
              <span>Branding or marketing</span>
            </label>
          </fieldset>

          <button type="submit" disabled={submitState.status === 'sending'}>
            {submitState.status === 'sending' ? 'Sending...' : 'Send Project Brief'}
          </button>
          {submitState.message && (
            <p className={`contact-form-status ${submitState.status}`} role="status">
              {submitState.message}
            </p>
          )}
        </form>
      </div>
    </section>
  );
}

function FooterSection() {
  const companyLinks = [
    { label: 'Home', href: '#home' },
    { label: 'Services', href: '#services' },
    { label: 'Why Us', href: '#why-vegaralabs' },
    { label: 'About', href: '#about-us' },
    { label: 'FAQ', href: '#support' },
    { label: 'Contact us', href: '#contact' },
  ];

  return (
    <footer className="agency-footer">
      <div className="footer-main">
        <a className="footer-brand" href="#home" aria-label="VegaraLabs home">
          <img src="/images/logo.svg" alt="" aria-hidden="true" />
          <strong>VegaraLabs</strong>
        </a>

        <nav className="footer-links" aria-label="Company">
          {companyLinks.map((link) => (
            <a href={link.href} key={link.href}>
              {link.label}
            </a>
          ))}
        </nav>
      </div>

      <div className="footer-bottom">
        <div className="footer-socials" aria-label="Social links">
          <a
            href="https://www.linkedin.com/in/vegara-labs-340860429"
            aria-label="LinkedIn"
            target="_blank"
            rel="noreferrer"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M6.94 8.98H3.54V20h3.4V8.98ZM5.24 4C4.16 4 3.4 4.72 3.4 5.68c0 .94.74 1.68 1.8 1.68h.02c1.1 0 1.8-.74 1.8-1.68C7 4.72 6.32 4 5.24 4ZM20.6 13.68c0-3.34-1.78-4.9-4.16-4.9-1.92 0-2.78 1.06-3.26 1.8v-1.6H9.8c.04 1.04 0 11.02 0 11.02h3.38v-6.16c0-.32.02-.66.12-.9.24-.66.82-1.34 1.78-1.34 1.26 0 1.76 1 1.76 2.46V20h3.38l.38-6.32Z" />
            </svg>
          </a>
        </div>
        <div className="footer-meta">
          <a href="#support">Status Page ↗</a>
          <a href="#support">Privacy Policy</a>
          <a href="#support">Cookie Settings</a>
          <span>Copyright 2026 © VegaraLabs</span>
        </div>
      </div>
    </footer>
  );
}

export default function Landing({ onSignIn, onGetStarted }) {
  return (
    <main className="landing agency-landing">
      <header className="agency-header">
        <a className="agency-logo" href="#home" aria-label="VegaraLabs home">
          <img src="/images/logo.svg" alt="VegaraLabs" />
        </a>

        <nav className="agency-nav" aria-label="Primary navigation">
          {NAV_ITEMS.map((item) => (
            <a href={item.href} key={item.href}>
              {item.label}
              <span>+</span>
            </a>
          ))}
        </nav>

        <a href="#contact" className="agency-contact">
          <span>Contact us</span>
          <b aria-hidden="true">↗</b>
        </a>
      </header>

      <section className="agency-hero" id="home">
        <div className="agency-hero-copy">
          <h1>
            <span>TRANSFORMING IDEAS</span>
            <span>INTO POWERFUL</span>
            <span><em>WEB SOLUTIONS.</em></span>
          </h1>
          <p>
            We make smart web solutions for any business, from booking systems and dashboards
            to digital tools that keep your team organised and your customers connected.
          </p>
          <div className="agency-actions">
            <button type="button" className="agency-primary" onClick={onGetStarted}>
              Start Your Project
            </button>
            <button type="button" className="agency-secondary" onClick={onSignIn}>
              Book a Free Consultation
            </button>
          </div>
        </div>

        <div className="agency-visual" aria-label="Astronaut hero artwork">
          <img src="/images/hero-astronaut.png" alt="Meditating astronaut in an orange space suit" />
          <div className="agency-tag tag-marketing">Marketing</div>
          <div className="agency-tag tag-development">Development</div>
          <div className="agency-tag tag-design">Design</div>
          <div className="agency-tag tag-video">Video</div>
        </div>
      </section>

      <ServicesOrbitSection />

      <WhyChooseSection />

      <AboutSection />

      <FaqSection />

      <ContactSection />

      <FooterSection />
    </main>
  );
}
